<template>
  <div style="min-width: 260px">
    <div class="game-player-info" v-if="isLoading">
      <p>Waiting for player</p>
    </div>
    <div class="game-player-info" v-else>
      <div class="game-player-info__left">
        <div class="image is-64x64">
          <Avatar :player="player" :moveAction="moveAction"></Avatar>
        </div>

        <GameResult
          v-show="score != null"
          :result="result"
          :score="score"
          class="game-player-info__result"
        />
      </div>

      <div class="game-player-info__right">
        <p class="is-size-4 has-text-weight-bold is-flex is-align-items-center">
          <span
            v-if="color"
            style="
              font-size: 2.25rem;
              padding-bottom: 0.5rem;
              line-height: 1rem;
            "
          >
            {{ color === "white" ? "♔" : "♚" }}
          </span>
          <a
            v-if="
              player &&
              player.username &&
              !isAiPlayer &&
              (!playerColor || result)
            "
            @click="gotoProfile"
          >
            <b-tooltip label="View Profile" position="is-right" :delay="200">
              <b>{{ playerName }}</b>
            </b-tooltip>
          </a>
          <b v-else>{{ playerName }}</b>
        </p>
        <p class="is-size-6">
          🏫
          <i>{{ schoolName }}</i>
        </p>
        <PlayerRating
          v-if="rating"
          :rating="rating"
          :diff="rating_diff"
          type="large"
        />
      </div>

      <!-- Status tag -->
      <StatusTag
        v-if="status"
        style="position: absolute; top: 0; right: 0"
        :status="status"
      />
    </div>
  </div>
</template>

<script>
import { CHESS_SIDE, GAME_RESULT } from "vue-chess-shared/constants";
import GameResult from "@/components/games/GameResult";
import PlayerRating from "@/components/stats/PlayerRating";
import StatusTag from "@/components/statuses/StatusTag";
import Avatar from "@/components/players/Avatar";

import * as PlayerHelper from "vue-chess-shared/utils/player-helper";
import { isAiPlayer } from "vue-chess-shared/utils/ai-helper";

export default {
  components: { GameResult, PlayerRating, StatusTag, Avatar },
  name: "GamePlayerInfo",

  props: {
    color: {
      type: String,
      default: null,
    },
    gameData: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      rating: null,
      rating_diff: null,
    };
  },

  watch: {
    gameData: {
      deep: true,
      handler() {
        if (!this.gameData) {
          return;
        }

        const rating_new =
          this.color === CHESS_SIDE.WHITE
            ? this.gameData.player1_rating_new
            : this.gameData.player2_rating_new;
        this.rating = rating_new?.rating ?? this.player?.stats?.rating ?? 0;
        this.rating_diff = rating_new?.rating_diff;
      },
    },
  },

  computed: {
    player() {
      if (!this.gameData.match) return null;

      return this.color === CHESS_SIDE.WHITE
        ? this.gameData.match.player1
        : this.gameData.match.player2;
    },

    playerColor() {
      return this.gameData.playerColor;
    },

    result() {
      if (!this.gameData.match) return null;
      return this.gameData.match.result;
    },

    score() {
      if (!this.gameData.match) return null;

      return this.color === CHESS_SIDE.WHITE
        ? this.gameData.match.player_1_score
        : this.gameData.match.player_2_score;
    },

    status() {
      if (this.color === this.playerColor) {
        return null;
      }
      return this.gameData.opponentStatus;
    },

    isLoading() {
      return !this.player || !this.player.first_name;
    },

    playerName() {
      return PlayerHelper.playerName(this.player);
    },

    schoolName() {
      if (!this.player || !this.player.school) return "";
      return this.player.school.school_name;
    },

    isAiPlayer() {
      return isAiPlayer(this.player);
    },

    moveAction() {
      if (
        [
          GAME_RESULT.CHECKMATE,
          GAME_RESULT.TIMEOUT,
          GAME_RESULT.FORFEIT,
          GAME_RESULT.RESIGN,
        ].includes(this.result) &&
        this.score
      ) {
        return "victory";
      }

      const lastMove = this.gameData.movesStats.at(-1);
      if (!lastMove) {
        return "idle";
      }

      // it's my move, check if it's a captured move
      if (lastMove.color === this.color && lastMove.move.includes("x")) {
        return "capture";
      }

      return "idle";
    },
  },

  methods: {
    gotoProfile() {
      if (!this.player || !this.player.username) return;
      this.$router.push({
        name: "profile",
        query: { u: this.player.username },
      });
    },
  },
};
</script>

<style scoped>
.game-player-info {
  min-height: 120px;
  display: flex;
  align-items: center;
  width: 100%;
  text-align: left;
  position: relative;
}

.game-player-info__left {
  padding: 5px;
  flex: 0.25;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
}

.game-player-info__right {
  padding: 5px;
  flex: 0.75;
}

.game-player-info__result {
  position: absolute;
  bottom: 0px;
}
</style>
